import { Component, DestroyRef, OnInit } from '@angular/core';
import { NgClass } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MatDialogRef } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { MatOption, MatSelect } from '@angular/material/select';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';

import { EMPTY, filter, forkJoin } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';

import { BaseItem, ProcessTemplate } from '@app/shared/models';
import { validateForm, CustomValidators } from '@app/shared/utils';
import { ProcessService } from '@app/services/process.service';
import { DirectoriesService } from '@app/services/directories.service';
import {
  DirectoryTypeEnum,
  DynamicDirectoriesEnum
} from '@app/enums/directories/directory.enum';
import { ConfirmationDialogService } from '@app/services/confirmation-dialog.service';
import { PrListService } from '@app/pages/procurement/pr/pr-container/pr-list/services/pr-list.service';

@Component({
  selector: 'app-create-pr-modal',
  imports: [
    ReactiveFormsModule,
    MatLabel,
    MatFormField,
    MatInput,
    NgClass,
    MatButton,
    MatTooltip,
    MatError,
    TranslatePipe,
    MatSelect,
    MatOption,
    CdkDrag,
    CdkDragHandle
  ],
  templateUrl: './create-pr-modal.component.html',
  styleUrl: './create-pr-modal.component.scss'
})
export class CreatePrModalComponent implements OnInit {
  public isCreateDisabled = false;
  public selectedProcessTemplateGroup = false;
  public form: FormGroup;

  public processTemplates: ProcessTemplate[] = [];
  public filteredProcessTemplates: ProcessTemplate[] = [];
  public processTemplateGroups: BaseItem[] = [];
  public currencies: BaseItem[] = [];

  private readonly UAH = 'UAH';
  private readonly processTypeId = '675e9a5d-c4e6-4c2c-5f9d-08dce3932db7';

  constructor(
    private fb: FormBuilder,
    private processService: ProcessService,
    private prListService: PrListService,
    private directoriesService: DirectoriesService,
    private confirmService: ConfirmationDialogService,
    private router: Router,
    private destroyRef: DestroyRef,
    private dialogRef: MatDialogRef<CreatePrModalComponent>
  ) {}

  ngOnInit(): void {
    this.getBaseData();
    this.initializeForm();
  }

  public onClose(): void {
    this.confirmService
      .openDialog({
        message: 'CONFIRM_CANCEL_CREATION_LONG'
      })
      .pipe(filter(Boolean))
      .subscribe(() => {
        this.dialogRef.close();
      });
  }

  public onCreate(): void {
    validateForm(this.form);

    if (this.form.valid) {
      this.createPr();
    }
  }

  public selectProcessTemplate(processTemplate: ProcessTemplate): void {
    const processTemplateIdControl = this.form.controls['processTemplateId'];

    if (processTemplateIdControl.value === processTemplate.id) {
      return;
    }

    processTemplateIdControl.setValue(processTemplate.id);
    this.form.updateValueAndValidity();
  }

  public selectProcessTemplateGroup(processTemplateGroup: BaseItem): void {
    const procedureTypeIdControl = this.form.controls['procedureTypeId'];

    if (procedureTypeIdControl.value === processTemplateGroup.id) {
      return;
    }

    this.filteredProcessTemplates = this.processTemplates.filter(
      processTemplate =>
        processTemplate.processTemplateGroupDynamicDirectoryId ===
        processTemplateGroup.id
    );
    procedureTypeIdControl.setValue(processTemplateGroup.id);
    this.form.controls['procedureTypeName'].setValue(processTemplateGroup.name);
    this.form.controls['processTemplateId'].setValue(null);
    this.selectedProcessTemplateGroup = true;
  }

  private createPr(): void {
    this.isCreateDisabled = true;

    this.prListService
      .createPr({
        ...this.form.value,
        mainCurrency: this.form.value.currency.id,
        mainCurrencyValue: this.form.value.currency.name
      })
      .subscribe({
        next: ({ id }) => {
          this.dialogRef.close();

          this.router.navigate(['/']).then(() => {
            this.router.navigate([`/procurement/pr/pr-list/${id}`]);
          });
        },
        error: () => {
          this.isCreateDisabled = false;

          return EMPTY;
        }
      });
  }

  private getBaseData(): void {
    forkJoin([
      this.processService.getProcessList(),
      this.directoriesService.getDirectoryEntries(
        DynamicDirectoriesEnum.ProcessTemplateGroups,
        DirectoryTypeEnum.Dynamic
      ),
      this.directoriesService.getDirectoryEntries(
        DynamicDirectoriesEnum.Currencies,
        DirectoryTypeEnum.Dynamic
      )
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([processTemplates, processTemplateGroups, currencies]) => {
        const filteredPTIds: string[] = [];
        this.processTemplates = processTemplates.filter(pt => {
          if (pt.processTypeId === this.processTypeId) {
            filteredPTIds.push(pt.processTemplateGroupDynamicDirectoryId);
            return true;
          }
          return false;
        });

        this.processTemplateGroups = processTemplateGroups.dataList
          .map(ptg =>
            filteredPTIds.includes(ptg.id[0])
              ? {
                  id: ptg.id[0],
                  name: Array.isArray(ptg['name']) ? ptg['name'][0] : ''
                }
              : null
          )
          .filter(Boolean) as BaseItem[];

        this.currencies = currencies.dataList
          .map(c => ({
            id: c.id[0],
            name: Array.isArray(c['short name']) ? c['short name'][0] : ''
          }))
          .filter(Boolean);

        this.selectProcessTemplateGroup(this.processTemplateGroups[0]);

        const defaultCurrency = this.currencies.find(c => c.name === this.UAH);

        this.form.get('currency')?.setValue(defaultCurrency);
      });
  }

  private initializeForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, CustomValidators.noWhiteSpace]],
      procedureTypeId: ['', Validators.required],
      currency: ['', Validators.required],
      procedureTypeName: ['', Validators.required],
      processTemplateId: ['', Validators.required]
    });
  }
}
